import React, { useSyncExternalStore } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import useBeforeUnloadGuard from './useBeforeUnloadGuard'
import { contagem } from '../../utils/localReset/messages'

export const STEP_LABELS = {
  saida: [
    'Enviando o que falta ao servidor',
    'Salvando o arquivo de backup',
    'Apagando os dados deste aparelho',
    'Saindo',
  ],
  limpeza: [
    'Enviando o que falta ao servidor',
    'Salvando o arquivo de backup',
    'Limpando os dados deste aparelho',
    'Baixando os dados de novo',
  ],
}

const actionBtn = (label, onClick, props = {}) => (
  <Button key={label} onClick={onClick} {...props}>
    {label}
  </Button>
)

export default function ResetFlowOverlay({ controller, title, onClose }) {
  const view = useSyncExternalStore(controller.subscribe, controller.getView)
  const { actions } = controller
  useBeforeUnloadGuard(view.phase !== 'finishing' && view.phase !== 'error')
  const labels = STEP_LABELS[view.mode] || STEP_LABELS.saida
  const b = view.backup

  return (
    <Dialog
      open
      fullScreen
      onClose={() => {}} // não dispensável: Esc e clique fora são ignorados
      aria-labelledby='reset-flow-title'
      // Conteúdo no centro da tela cheia (antes: colado no topo, 2/3 em branco; QA HML U8).
      slotProps={{
        paper: {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          },
        },
      }}
    >
      <Box sx={{ maxWidth: 560, mx: 'auto', p: 3, py: 4, width: '100%' }}>
        <Typography id='reset-flow-title' variant='h5' sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Mantenha esta aba aberta e com internet. Isso pode levar alguns
          minutos.
        </Typography>

        <List dense aria-label='Passos'>
          {labels.map((label, i) => {
            const n = i + 1
            const done = n < view.step
            // O passo que parou não fica com o anel de "em andamento" (QA HML U8).
            const failed =
              n === view.step &&
              (view.phase === 'blocked' || view.phase === 'error')
            return (
              <ListItem key={label} disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {done ? (
                    <CheckCircleIcon color='success' />
                  ) : failed ? (
                    <ErrorOutlineIcon
                      color='warning'
                      aria-label='Passo com problema'
                    />
                  ) : (
                    <RadioButtonUncheckedIcon
                      color={n === view.step ? 'primary' : 'disabled'}
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={`Passo ${n} de ${labels.length}: ${label}`}
                  slotProps={{
                    primary: { fontWeight: n === view.step ? 600 : 400 },
                  }}
                />
              </ListItem>
            )
          })}
        </List>

        {(view.phase === 'running' || view.phase === 'finishing') && (
          <Box sx={{ mt: 2 }} role='status'>
            <Typography variant='body2' sx={{ mb: 1 }}>
              {view.message}
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {view.phase === 'blocked' && view.blocked && (
          <Box sx={{ mt: 2 }}>
            <Alert severity='warning'>{view.blocked.message}</Alert>
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
              }}
            >
              {actionBtn('Cancelar', actions.cancel)}
              {actionBtn('Tentar de novo', actions.retry, {
                variant: 'contained',
              })}
            </Box>
          </Box>
        )}

        {view.phase === 'backup' && b && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='subtitle1' sx={{ mb: 1 }}>
              Passo 2 de {labels.length}: salve o backup
            </Typography>
            <Typography variant='body2' sx={{ mb: 1 }}>
              Arquivo: {b.filename} (~{b.sizeKb} KB,{' '}
              {contagem(b.farmsCount, 'fazenda', 'fazendas')}). Guarde-o: ele é
              a cópia dos seus dados.
            </Typography>
            {b.warnings.map((w) => (
              <Alert key={w} severity='error' sx={{ mb: 1 }}>
                {w}
              </Alert>
            ))}
            {b.notice && (
              <Alert severity='info' sx={{ mb: 1 }}>
                {b.notice}
              </Alert>
            )}
            {b.canUsePicker ? (
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'flex-end',
                }}
              >
                {actionBtn('Cancelar operação', actions.cancel)}
                {actionBtn('Salvar backup', actions.saveBackup, {
                  variant: 'contained',
                  disabled: b.saving,
                })}
              </Box>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Button variant='outlined' onClick={actions.downloadBackup}>
                  Baixar backup
                </Button>
                <FormControlLabel
                  sx={{ display: 'block', mt: 1 }}
                  control={
                    <Checkbox
                      checked={b.confirmedChecked}
                      disabled={!b.downloaded}
                      onChange={(e) => actions.setConfirmed(e.target.checked)}
                    />
                  }
                  label='Confirmo que o arquivo de backup foi salvo'
                />
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'flex-end',
                  }}
                >
                  {actionBtn('Cancelar operação', actions.cancel)}
                  {actionBtn('Continuar', actions.continueAfterDownload, {
                    variant: 'contained',
                    disabled: !(b.downloaded && b.confirmedChecked),
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {view.phase === 'error' && (
          <Box sx={{ mt: 2 }}>
            <Alert severity='info'>{view.error}</Alert>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              {actionBtn('Fechar', onClose, { variant: 'contained' })}
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  )
}
